/**
 * Personalized Action Plan (2026-08-28). The self-serve product that replaced EOB
 * Review on the pricing page: pay $49, upload the bill (and the EOB if
 * there is one), answer a few questions, get a written plan back within
 * 24 hours of the last document landing.
 *
 * Three screens, one primary object each:
 *   1. the case file form (documents, then a short intake)
 *   2. the waiting screen while the plan is written
 *   3. the plan itself
 *
 * EobReview.tsx is the older sibling and stays in the codebase, unrouted
 * from the pricing page but still reachable for existing EOB accounts.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import GlobalLoader from '../components/context/GlobalLoader';
import { API_BASE } from '../lib/apiConfig';
import { getMetaTrackingContext } from '../lib/checkoutContext';
import {
  phActionPlanSubmitted,
  phActionPlanViewed,
  phFailure,
  phInitiateCheckout,
} from '../lib/posthog';
import {
  ACCEPTED_DOC_EXTENSIONS,
  ACCEPTED_DOC_TYPES,
  BILL_FOR_OPTIONS,
  BILL_STATUS_OPTIONS,
  CONTACTED_OPTIONS,
  DEADLINE_OPTIONS,
  FA_STATUS_OPTIONS,
  GOAL_OPTIONS,
  HOUSEHOLD_SIZE_OPTIONS,
  INCOME_BAND_OPTIONS,
  INSURANCE_OPTIONS,
  LUMP_SUM_OPTIONS,
  MAX_DOC_BYTES,
  MEDICAID_OPTIONS,
  MONTHLY_BUDGET_OPTIONS,
  VISIT_TYPE_OPTIONS,
  YES_NO,
  YES_NO_UNSURE,
  planDocumentPath,
  plansRemaining,
  type ActionPlanIntake,
  type ActionPlanRow,
  type PlanDocument,
} from '../lib/actionPlan';
import { parsePlanMarkdown, type InlineNode, type PlanBlock } from '../lib/planMarkdown';
import { prepareImageForUpload } from '../lib/imageUpload';
import '@styles/pages/eob.css';
import '@styles/pages/action-plan.css';

const PLAN_PRICE = 49;
const BUCKET = 'medical_bills';

/* ── Helpers ─────────────────────────────────────────────── */
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDay(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/* The promise is 24 hours from the case file landing. Same arithmetic as the
   "case file received" email, shown in the browser's own time zone. */
const TURNAROUND_MS = 24 * 60 * 60 * 1000;
function dueAt(plan: ActionPlanRow): Date {
  return new Date(new Date(plan.submitted_at ?? plan.created_at).getTime() + TURNAROUND_MS);
}
function formatDue(date: Date) {
  const day = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

function isAccepted(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const okType = ACCEPTED_DOC_TYPES.includes(file.type) || ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext);
  if (!okType) return `${file.name} is not a PDF or image.`;
  if (file.size > MAX_DOC_BYTES) return `${file.name} is over 15MB.`;
  return null;
}

function renderInlines(inlines: InlineNode[]): ReactNode[] {
  return inlines.map((node, i) => {
    if (node.type === 'bold') return <strong key={i}>{node.text}</strong>;
    if (node.type === 'link') {
      return (
        <a key={i} href={node.href} target="_blank" rel="noopener noreferrer">
          {node.text}
        </a>
      );
    }
    return <span key={i}>{node.text}</span>;
  });
}

function PlanBody({ blocks }: { blocks: PlanBlock[] }) {
  return (
    <div className="ap-plan">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'heading': {
            const Tag = block.level === 1 ? 'h2' : block.level === 2 ? 'h3' : 'h4';
            return <Tag key={i}>{renderInlines(block.inlines)}</Tag>;
          }
          case 'paragraph':
            return <p key={i}>{renderInlines(block.inlines)}</p>;
          case 'bullets':
            return (
              <ul key={i}>
                {block.items.map((item, j) => <li key={j}>{renderInlines(item)}</li>)}
              </ul>
            );
          case 'numbered':
            return (
              <ol key={i}>
                {block.items.map((item, j) => <li key={j}>{renderInlines(item)}</li>)}
              </ol>
            );
          case 'quote':
            return <blockquote key={i}>{renderInlines(block.inlines)}</blockquote>;
          case 'divider':
            return <hr key={i} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ACTION PLAN PAGE
══════════════════════════════════════════════════════════ */
export default function ActionPlan() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState<ActionPlanRow[]>([]);
  const [billsLimit, setBillsLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [wantsNew, setWantsNew] = useState(() => searchParams.get('new') === '1');
  const [buying, setBuying] = useState(false);

  /* A draft is a case file the customer started but has not sent in. It
     is saved on the server as it is built, so a refresh (or another
     device) lands back in the same place. One open draft per account. */
  const draft = useMemo(() => plans.find((p) => p.status === 'draft') ?? null, [plans]);
  const sent = useMemo(() => plans.filter((p) => p.status !== 'draft'), [plans]);

  const used = plans.length; // a draft already uses up its plan
  const remaining = plansRemaining(billsLimit, used);
  const selected = useMemo(
    () => sent.find((p) => p.id === selectedId) ?? sent[0] ?? null,
    [sent, selectedId],
  );

  /* ── Load plans + entitlement ── */
  const load = useCallback(async () => {
    if (!user) return;
    const [plansRes, subRes] = await Promise.all([
      supabase
        .from('action_plans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('subscriptions')
        .select('bills_limit, plan, status')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    setPlans((plansRes.data ?? []) as ActionPlanRow[]);
    if (subRes.data && subRes.data.plan === 'action_plan' && subRes.data.status === 'active') {
      setBillsLimit(subRes.data.bills_limit ?? 0);
    } else {
      setBillsLimit(0);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  /* The sidebar's "Another bill" button lands here with ?new=1, also when
     the page is already open, so watch the param rather than reading it
     once. */
  useEffect(() => {
    if (searchParams.get('new') === '1') setWantsNew(true);
  }, [searchParams]);

  /* ── Live updates when the plan is delivered ── */
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`action-plans-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'action_plans',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        const row = payload.new as ActionPlanRow;
        setPlans((prev) => prev.map((p) => (p.id === row.id ? { ...p, ...row } : p)));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  /* ── Back from Stripe after buying another plan ──
     URL params alone never count as a purchase: /stripe/verify-addon
     confirms the session is paid before we say anything. */
  useEffect(() => {
    if (searchParams.get('addon') !== 'success') return;
    const sessionId = searchParams.get('session_id');
    window.history.replaceState({}, '', '/action-plan');
    if (!sessionId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/stripe/verify-addon`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        if (!res.ok) throw new Error(`verify-addon ${res.status}`);
        showToast('Payment received. Send in the next case file whenever you are ready.', 'success');
        setWantsNew(true);
        await load();
      } catch (err) {
        phFailure({ step: 'action_plan_verify_addon', message: (err as Error).message });
      }
    })();
  }, [searchParams, showToast, load]);

  /* ── Buy a plan for another bill (existing customer) ── */
  async function handleBuyAnother() {
    if (!user || buying) return;
    setBuying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${API_BASE}/stripe/create-addon-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(getMetaTrackingContext()),
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Checkout failed');
      phInitiateCheckout('addon', { flow: 'action_plan_addon', value: PLAN_PRICE, plan: 'action_plan' });
      window.location.href = data.url;
    } catch (err) {
      phFailure({ step: 'checkout_create_action_plan_addon', message: (err as Error).message });
      showToast('Something went wrong. Please try again.', 'error');
      setBuying(false);
    }
  }

  if (loading) return <GlobalLoader message="Opening your case file…" />;

  /* An open draft always wins: that is the thing they were in the middle
     of. Otherwise the form shows when there is a plan left to use and
     nothing to look at yet, or they asked for a new one. */
  const showForm = draft !== null || (remaining > 0 && (sent.length === 0 || wantsNew));

  if (showForm && user) {
    return (
      <CaseFileForm
        userId={user.id}
        draft={draft}
        onDraftChange={(row) =>
          setPlans((prev) => (prev.some((p) => p.id === row.id)
            ? prev.map((p) => (p.id === row.id ? row : p))
            : [row, ...prev]))
        }
        onCancel={sent.length > 0 ? () => setWantsNew(false) : undefined}
        onSubmitted={(row) => {
          setPlans((prev) => prev.map((p) => (p.id === row.id ? row : p)));
          setSelectedId(row.id);
          setWantsNew(false);
        }}
      />
    );
  }

  if (!selected) {
    /* Paid for nothing that is still open, e.g. a refunded account. */
    return (
      <div className="eob-container">
        <span className="eob-eyebrow">Personalized Action Plan</span>
        <h1 className="eob-heading">No plan on file <span className="italic">yet.</span></h1>
        <p className="eob-subheading">
          There is no Personalized Action Plan attached to this account. If you think that is wrong, write to{' '}
          <a href="mailto:help@mediloop.ai" className="ap-link">help@mediloop.ai</a> and we will sort it out.
        </p>
      </div>
    );
  }

  return (
    <div className="eob-container">
      {selected.status === 'delivered' ? (
        <DeliveredPlan plan={selected} />
      ) : (
        <WaitingPlan plan={selected} email={user?.email ?? null} onDocumentsChanged={(docs) =>
          setPlans((prev) => prev.map((p) => (p.id === selected.id ? { ...p, documents: docs } : p)))
        } />
      )}

      {/* Foot: the only selling lines on the page, and never more than two.
          The upgrade rule is stated as a rule; /plan knows whether this
          account is still inside it. Buying a second plan is only offered
          once the first one has been delivered: pitching it to someone who
          is still waiting on their first plan is noise. */}
      <div className="ap-foot">
        <p>
          Rather have us negotiate it? Upgrade within 14 days of buying your plan and your $49 is credited.{' '}
          <Link to="/plan" className="ap-link">See the options</Link>
        </p>
        {remaining > 0 ? (
          <p>
            You have {remaining === 1 ? 'a plan' : `${remaining} plans`} ready to use on another bill.{' '}
            <button type="button" className="ap-link" onClick={() => setWantsNew(true)}>
              Send in the next case file
            </button>
          </p>
        ) : selected.status === 'delivered' ? (
          <p>
            Have another bill? A plan for it is ${PLAN_PRICE}.{' '}
            <button type="button" className="ap-link" onClick={handleBuyAnother} disabled={buying}>
              {buying ? 'Opening checkout…' : 'Get a plan for another bill'}
            </button>
          </p>
        ) : null}
      </div>

      {sent.length > 1 && (
        <div className="ap-history">
          <div className="ap-history-label">Your plans</div>
          {sent.map((p) => {
            const bill = p.documents.find((d) => d.kind === 'bill');
            const title = p.intake?.provider_name || bill?.name || 'Bill';
            return (
              <button
                key={p.id}
                type="button"
                className={`ap-history-row ${p.id === selected.id ? 'is-current' : ''}`}
                onClick={() => setSelectedId(p.id)}
              >
                <span className="ap-history-title">{title}</span>
                <span className="ap-history-meta">
                  {p.status === 'delivered'
                    ? `Delivered ${formatDay(p.delivered_at ?? p.updated_at)}`
                    : `Sent ${formatDay(p.submitted_at ?? p.created_at)}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   1. CASE FILE FORM: documents, then a short intake.
   Everything is saved to the draft row as it happens: each file the
   moment it uploads, each answer a beat after it changes. "Send in the
   case file" is the only step that changes status.
══════════════════════════════════════════════════════════ */
const stepKey = (draftId: string) => `ap-step-${draftId}`;

function CaseFileForm({
  userId,
  draft,
  onDraftChange,
  onCancel,
  onSubmitted,
}: {
  userId: string;
  draft: ActionPlanRow | null;
  onDraftChange: (row: ActionPlanRow) => void;
  onCancel?: () => void;
  onSubmitted: (row: ActionPlanRow) => void;
}) {
  const { showToast } = useToast();
  const billInputRef = useRef<HTMLInputElement>(null);
  const eobInputRef = useRef<HTMLInputElement>(null);
  const billCameraRef = useRef<HTMLInputElement>(null);
  const eobCameraRef = useRef<HTMLInputElement>(null);

  /* The draft row is the source of truth for documents. A ref keeps the
     latest copy reachable from async upload loops without stale closures. */
  const draftRef = useRef<ActionPlanRow | null>(draft);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  const [step, setStep] = useState<1 | 2>(() => {
    if (!draft) return 1;
    try { return localStorage.getItem(stepKey(draft.id)) === '2' ? 2 : 1; } catch { return 1; }
  });
  const [pending, setPending] = useState<Array<{ id: string; name: string; kind: 'bill' | 'eob' }>>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const [intake, setIntake] = useState<ActionPlanIntake>(() => ({ goals: [], ...(draft?.intake ?? {}) }));
  const intakeTouched = useRef(false);
  const set = <K extends keyof ActionPlanIntake>(key: K, value: ActionPlanIntake[K]) => {
    intakeTouched.current = true;
    setIntake((prev) => ({ ...prev, [key]: value }));
  };

  const documents = draft?.documents ?? [];
  const billDocs = documents.filter((d) => d.kind === 'bill');
  const eobDocs = documents.filter((d) => d.kind === 'eob');

  /* ── Persist a documents change on the draft (creating it on first use) ── */
  async function writeDocuments(next: PlanDocument[]): Promise<ActionPlanRow> {
    const current = draftRef.current;
    if (current) {
      const { data, error: updErr } = await supabase
        .from('action_plans')
        .update({ documents: next })
        .eq('id', current.id)
        .select()
        .single();
      if (updErr) throw updErr;
      const row = data as ActionPlanRow;
      draftRef.current = row;
      onDraftChange(row);
      return row;
    }
    const { data, error: insErr } = await supabase
      .from('action_plans')
      .insert({ user_id: userId, status: 'draft', documents: next, intake })
      .select()
      .single();
    if (insErr) {
      /* 23505: a draft already exists (another tab got there first). Pick
         it up and add to it instead of failing the upload. */
      if (insErr.code === '23505') {
        const { data: existing } = await supabase
          .from('action_plans')
          .select('*')
          .eq('user_id', userId)
          .eq('status', 'draft')
          .maybeSingle();
        if (existing) {
          draftRef.current = existing as ActionPlanRow;
          return writeDocuments([...(existing as ActionPlanRow).documents, ...next]);
        }
      }
      throw insErr;
    }
    const row = data as ActionPlanRow;
    draftRef.current = row;
    onDraftChange(row);
    return row;
  }

  /* ── Files upload the moment they are chosen ── */
  async function addFiles(kind: 'bill' | 'eob', list: FileList | null) {
    if (!list) return;
    setError('');
    const files = Array.from(list);
    for (const picked of files) {
      const problem = isAccepted(picked);
      if (problem) { setError(problem); continue; }
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setPending((prev) => [...prev, { id: tempId, name: picked.name, kind }]);
      // iPhone photos come in as HEIC; turn them into JPEG where the browser can.
      const file = await prepareImageForUpload(picked);
      const path = planDocumentPath(userId, file.name);
      try {
        const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, file);
        if (uploadError) throw uploadError;
      } catch (err) {
        console.error('action plan upload failed:', err);
        phFailure({ step: 'action_plan_upload', message: (err as Error).message });
        setError(`${file.name} did not upload. Please try again.`);
        setPending((prev) => prev.filter((p) => p.id !== tempId));
        continue;
      }
      try {
        const doc: PlanDocument = { kind, path, name: file.name, size: file.size, type: file.type || undefined };
        await writeDocuments([...(draftRef.current?.documents ?? []), doc]);
      } catch (err) {
        /* The file is in storage but the case file could not record it (for
           example when the database is behind the app). Say so, and take the
           orphan back out so a retry starts clean. */
        console.error('action plan draft save failed:', err);
        phFailure({ step: 'action_plan_save_document', message: (err as Error).message });
        setError(`${file.name} uploaded, but we could not save it to your case file. Please try again in a moment.`);
        void supabase.storage.from(BUCKET).remove([path]);
      } finally {
        setPending((prev) => prev.filter((p) => p.id !== tempId));
      }
    }
  }

  async function removeFile(doc: PlanDocument) {
    const current = draftRef.current;
    if (!current) return;
    setError('');
    try {
      await writeDocuments(current.documents.filter((d) => d.path !== doc.path));
      // Best effort: the row is already right, a stray object is harmless.
      void supabase.storage.from(BUCKET).remove([doc.path]);
    } catch (err) {
      phFailure({ step: 'action_plan_remove_document', message: (err as Error).message });
      setError('Could not remove that file. Please try again.');
    }
  }

  /* ── Answers save a beat after they change ── */
  useEffect(() => {
    if (!intakeTouched.current) return;
    const current = draftRef.current;
    if (!current) return;
    setSaveState('saving');
    const t = setTimeout(async () => {
      const { data, error: updErr } = await supabase
        .from('action_plans')
        .update({ intake })
        .eq('id', current.id)
        .select()
        .single();
      if (updErr) {
        setSaveState('error');
        phFailure({ step: 'action_plan_intake_autosave', message: updErr.message });
        return;
      }
      draftRef.current = data as ActionPlanRow;
      onDraftChange(data as ActionPlanRow);
      setSaveState('saved');
    }, 600);
    return () => clearTimeout(t);
    // onDraftChange is stable enough for this; re-running on it would resave on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intake]);

  function goToStep(next: 1 | 2) {
    setError('');
    setStep(next);
    const current = draftRef.current;
    if (current) {
      try { localStorage.setItem(stepKey(current.id), String(next)); } catch { /* noop */ }
    }
  }

  function toggleGoal(value: string) {
    const goals = new Set(intake.goals ?? []);
    if (goals.has(value)) goals.delete(value); else goals.add(value);
    set('goals', Array.from(goals));
  }

  /* ── Send it in: the only status change the customer makes ── */
  async function handleSubmit() {
    const current = draftRef.current;
    if (!current || submitting) return;
    if (!current.documents.some((d) => d.kind === 'bill')) {
      setError('Add your bill first.');
      goToStep(1);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      /* Follow-up answers whose question was hidden again are dropped, so
         a case file never contradicts itself. */
      const cleanIntake: ActionPlanIntake = {
        ...intake,
        insurance_billed: intake.insurance?.startsWith('insured') ? intake.insurance_billed : undefined,
        estimate_given: intake.visit_type === 'scheduled' ? intake.estimate_given : undefined,
        deadline_date: intake.deadline_kind && intake.deadline_kind !== 'none' ? intake.deadline_date : undefined,
        contact_notes: intake.contacted && intake.contacted !== 'no' ? intake.contact_notes?.trim() || undefined : undefined,
        disputed_items: intake.disputed_items?.trim() || undefined,
        notes: intake.notes?.trim() || undefined,
      };

      const { data, error: submitError } = await supabase
        .from('action_plans')
        .update({ status: 'submitted', intake: cleanIntake })
        .eq('id', current.id)
        .select()
        .single();
      if (submitError) {
        phFailure({ step: 'action_plan_submit', message: submitError.message });
        throw new Error('We could not send your case file. Please try again.');
      }
      const row = data as ActionPlanRow;
      try { localStorage.removeItem(stepKey(current.id)); } catch { /* noop */ }

      phActionPlanSubmitted({
        planId: row.id,
        documentCount: row.documents.length,
        hasEob: row.documents.some((d) => d.kind === 'eob'),
        insurance: cleanIntake.insurance,
        billStatus: cleanIntake.bill_status,
      });

      // Starts the 24 hour clock on the ops side and sends the customer the
      // "case file received" email with the real deadline. Fire and forget:
      // the request is saved either way, and the admin queue shows it too.
      void (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          await fetch(`${API_BASE}/notify/action-plan-submitted`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              planId: row.id,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          });
        } catch (err) {
          console.warn('[action-plan] submit notification failed', err);
        }
      })();

      showToast(`Case file received. Your plan is ready by ${formatDue(dueAt(row))}.`, 'success');
      onSubmitted(row);
    } catch (err) {
      setError((err as Error).message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const uploadingCount = pending.length;

  const dropZone = (
    kind: 'bill' | 'eob',
    hasFiles: boolean,
    ref: React.RefObject<HTMLInputElement | null>,
    cameraRef: React.RefObject<HTMLInputElement | null>,
    title: string,
    hint: string,
  ) => (
    <div
      className={`eob-upload-box ${hasFiles ? 'has-file' : ''}`}
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
      onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
      onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); void addFiles(kind, e.dataTransfer.files); }}
    >
      <input
        ref={ref}
        type="file"
        multiple
        accept={ACCEPTED_DOC_EXTENSIONS}
        style={{ display: 'none' }}
        onChange={(e) => { void addFiles(kind, e.target.files); e.target.value = ''; }}
      />
      {/* A second input with `capture` opens the camera straight away on
          phones. Kept separate so the main picker still offers the photo
          library and files. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => { void addFiles(kind, e.target.files); e.target.value = ''; }}
      />
      <div className="eob-upload-icon">
        <Upload size={18} color="#E97A50" />
      </div>
      <div className="eob-upload-hint"><strong>{title}</strong> or drag and drop</div>
      <div className="eob-upload-hint" style={{ marginTop: 4 }}>{hint}</div>
      <button
        type="button"
        className="ap-link ap-camera"
        onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
      >
        Take a photo
      </button>
    </div>
  );

  const fileList = (kind: 'bill' | 'eob', docs: PlanDocument[]) => {
    const inFlight = pending.filter((p) => p.kind === kind);
    if (!docs.length && !inFlight.length) return null;
    return (
      <ul className="ap-files">
        {docs.map((d) => (
          <li key={d.path} className="ap-file">
            <FileText size={14} />
            <span className="ap-file-name">{d.name}</span>
            <button type="button" className="ap-link ap-file-remove" onClick={(e) => { e.stopPropagation(); void removeFile(d); }}>
              Remove
            </button>
          </li>
        ))}
        {inFlight.map((p) => (
          <li key={p.id} className="ap-file is-pending">
            <FileText size={14} />
            <span className="ap-file-name">{p.name}</span>
            <span className="ap-file-status">Uploading…</span>
          </li>
        ))}
      </ul>
    );
  };

  /* ── Step 1: documents ── */
  if (step === 1) {
    return (
      <div className="eob-container">
        <span className="eob-eyebrow">Personalized Action Plan</span>
        <h1 className="eob-heading">Send in <span className="italic">the case file.</span></h1>
        <p className="eob-subheading">
          Upload your bill, and your Explanation of Benefits if you have one. Then answer a few
          questions so the plan fits your situation. Your written plan lands within 24 hours of
          receiving all your documents.
        </p>

        {error && <div className="eob-error">{error}</div>}

        <div className="eob-field">
          <label className="eob-label">Your bill</label>
          {dropZone('bill', billDocs.length > 0, billInputRef, billCameraRef, 'Click to upload your bill', 'PDF, JPG or PNG, up to 15MB each. Add every page.')}
          {fileList('bill', billDocs)}
        </div>

        <div className="eob-field">
          <label className="eob-label">
            Explanation of Benefits <span className="optional">(optional, if you have insurance)</span>
          </label>
          {dropZone('eob', eobDocs.length > 0, eobInputRef, eobCameraRef, 'Click to upload your EOB', 'The statement from your insurer, not the bill.')}
          {fileList('eob', eobDocs)}
        </div>

        <button
          className="eob-submit-btn"
          onClick={() => goToStep(2)}
          disabled={!billDocs.length || uploadingCount > 0}
        >
          {uploadingCount > 0 ? 'Uploading…' : 'Continue'}
        </button>
        <p className="ap-back">
          {draft ? 'Your files are saved. You can come back to this any time.' : ''}
          {onCancel && (
            <>
              {draft ? ' ' : ''}
              <button type="button" className="ap-link" onClick={onCancel}>Back to your plans</button>
            </>
          )}
        </p>
      </div>
    );
  }

  /* ── Step 2: intake ── */
  const radioGroup = (
    name: keyof ActionPlanIntake,
    options: ReadonlyArray<{ value: string; label: string }>,
  ) => (
    <div className="ap-choices" role="radiogroup">
      {options.map((o) => (
        <label key={o.value} className="ap-choice">
          <input
            type="radio"
            name={String(name)}
            value={o.value}
            checked={intake[name] === o.value}
            onChange={() => set(name, o.value as never)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="eob-container">
      <span className="eob-eyebrow">Personalized Action Plan</span>
      <h1 className="eob-heading">A few <span className="italic">details.</span></h1>
      <p className="eob-subheading">
        The bill already tells us the provider, the dates and the amounts. These are the things it
        can&apos;t. Everything is optional, skip anything you are not sure about, and your answers save
        as you go.
      </p>

      {error && <div className="eob-error">{error}</div>}

      <div className="eob-field">
        <label className="eob-label">Your insurance situation</label>
        {radioGroup('insurance', INSURANCE_OPTIONS)}
      </div>

      {intake.insurance?.startsWith('insured') && (
        <div className="eob-field">
          <label className="eob-label">Was your insurance billed for this visit?</label>
          {radioGroup('insurance_billed', YES_NO_UNSURE)}
        </div>
      )}

      <div className="ap-grid-2">
        <div className="eob-field">
          <label className="eob-label">Who is this bill for?</label>
          {radioGroup('bill_for', BILL_FOR_OPTIONS)}
        </div>
        <div className="eob-field">
          <label className="eob-label">How did the visit come about?</label>
          {radioGroup('visit_type', VISIT_TYPE_OPTIONS)}
        </div>
      </div>

      {intake.visit_type === 'scheduled' && (
        <div className="eob-field">
          <label className="eob-label">Did you get a written estimate beforehand?</label>
          {radioGroup('estimate_given', YES_NO_UNSURE)}
        </div>
      )}

      <div className="eob-field">
        <label className="eob-label">Are there other bills from the same visit?</label>
        {radioGroup('other_bills', YES_NO_UNSURE)}
        <p className="ap-help">Anesthesia, radiology and ambulance often bill separately. You can add them after sending this in.</p>
      </div>

      <div className="eob-field">
        <label className="eob-label">Where things stand with this bill</label>
        {radioGroup('bill_status', BILL_STATUS_OPTIONS)}
      </div>

      <div className="eob-field">
        <label className="eob-label">Is there a deadline you are up against?</label>
        {radioGroup('deadline_kind', DEADLINE_OPTIONS)}
        {intake.deadline_kind && intake.deadline_kind !== 'none' && (
          <input
            className="eob-input"
            style={{ marginTop: 10 }}
            type="date"
            value={intake.deadline_date ?? ''}
            onChange={(e) => set('deadline_date', e.target.value || undefined)}
          />
        )}
      </div>

      <div className="ap-grid-2">
        <div className="eob-field">
          <label className="eob-label">Have you asked for an itemized bill?</label>
          {radioGroup('itemized_requested', YES_NO_UNSURE)}
        </div>
        <div className="eob-field">
          <label className="eob-label">Have you contacted them about it?</label>
          {radioGroup('contacted', CONTACTED_OPTIONS)}
        </div>
      </div>

      {intake.contacted && intake.contacted !== 'no' && (
        <div className="eob-field">
          <label className="eob-label">What did they say? <span className="optional">(optional)</span></label>
          <textarea
            className="eob-textarea"
            placeholder="e.g. They offered a payment plan of $200 a month."
            value={intake.contact_notes ?? ''}
            onChange={(e) => set('contact_notes', e.target.value)}
          />
        </div>
      )}

      <div className="eob-field">
        <label className="eob-label">Has financial assistance come up?</label>
        {radioGroup('fa_status', FA_STATUS_OPTIONS)}
      </div>

      <div className="ap-grid-2">
        <div className="eob-field">
          <label className="eob-label">People in your household</label>
          <select
            className="eob-input ap-select"
            value={intake.household_size ?? ''}
            onChange={(e) => set('household_size', e.target.value || undefined)}
          >
            <option value="">Choose</option>
            {HOUSEHOLD_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="eob-field">
          <label className="eob-label">Household income, roughly</label>
          <select
            className="eob-input ap-select"
            value={intake.income_band ?? ''}
            onChange={(e) => set('income_band', e.target.value || undefined)}
          >
            <option value="">Choose</option>
            {INCOME_BAND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <p className="ap-help">Only used to check whether you qualify for financial assistance.</p>
        </div>
      </div>

      <div className="ap-grid-2">
        <div className="eob-field">
          <label className="eob-label">Has your income dropped recently?</label>
          {radioGroup('income_dropped', YES_NO)}
        </div>
        <div className="eob-field">
          <label className="eob-label">Have you applied for Medicaid?</label>
          {radioGroup('medicaid_status', MEDICAID_OPTIONS)}
        </div>
      </div>

      <div className="ap-grid-2">
        <div className="eob-field">
          <label className="eob-label">Could you pay a lump sum if the total came down?</label>
          {radioGroup('lump_sum', LUMP_SUM_OPTIONS)}
        </div>
        <div className="eob-field">
          <label className="eob-label">What could you manage per month?</label>
          {radioGroup('monthly_budget', MONTHLY_BUDGET_OPTIONS)}
        </div>
      </div>

      <div className="eob-field">
        <label className="eob-label">What would help most?</label>
        <div className="ap-choices">
          {GOAL_OPTIONS.map((o) => (
            <label key={o.value} className="ap-choice">
              <input
                type="checkbox"
                checked={(intake.goals ?? []).includes(o.value)}
                onChange={() => toggleGoal(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="eob-field">
        <label className="eob-label">Anything on the bill that didn't happen, or that you don't recognize? <span className="optional">(optional)</span></label>
        <textarea
          className="eob-textarea"
          placeholder="e.g. A $900 room charge when I was only in the ER."
          value={intake.disputed_items ?? ''}
          onChange={(e) => set('disputed_items', e.target.value)}
        />
      </div>

      <div className="eob-field">
        <label className="eob-label">Anything else we should know? <span className="optional">(optional)</span></label>
        <textarea
          className="eob-textarea"
          placeholder="e.g. I already paid $300 of it, or I moved and the letters go to my old address."
          value={intake.notes ?? ''}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <button className="eob-submit-btn" onClick={handleSubmit} disabled={submitting || saveState === 'saving'}>
        {submitting ? 'Sending…' : 'Send in the case file'}
      </button>
      <p className="ap-back">
        <span className="ap-save-state">
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved.' : saveState === 'error' ? 'Could not save your last answer, check your connection.' : ''}
        </span>
        {' '}
        <button type="button" className="ap-link" onClick={() => goToStep(1)} disabled={submitting}>
          Back to documents
        </button>
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   2. WAITING: the plan is being written
══════════════════════════════════════════════════════════ */
function WaitingPlan({
  plan,
  email,
  onDocumentsChanged,
}: {
  plan: ActionPlanRow;
  email: string | null;
  onDocumentsChanged: (docs: PlanDocument[]) => void;
}) {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(false);

  async function addDocument(list: FileList | null) {
    const picked = list?.[0];
    if (!picked || adding) return;
    const problem = isAccepted(picked);
    if (problem) { showToast(problem, 'error'); return; }
    setAdding(true);
    try {
      const file = await prepareImageForUpload(picked);
      const path = planDocumentPath(plan.user_id, file.name);
      const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, file);
      if (uploadError) throw uploadError;
      const documents: PlanDocument[] = [
        ...plan.documents,
        { kind: 'other', path, name: file.name, size: file.size, type: file.type || undefined },
      ];
      const { error: updateError } = await supabase
        .from('action_plans')
        .update({ documents })
        .eq('id', plan.id);
      if (updateError) throw updateError;
      onDocumentsChanged(documents);
      showToast('Added to your case file.', 'success');
    } catch (err) {
      phFailure({ step: 'action_plan_add_document', message: (err as Error).message });
      showToast('That file did not upload. Please try again.', 'error');
    } finally {
      setAdding(false);
    }
  }

  return (
    <>
      <span className="eob-eyebrow">In progress</span>
      <h1 className="eob-heading">Agent Loop <span className="italic">is on it.</span></h1>
      <p className="eob-subheading">
        Your plan is being written. Nothing else is needed from you.
      </p>

      {/* The deadline is the one thing they came here to check, so it sits at
          the head of the card and is not repeated anywhere else on the page. */}
      <div className="eob-status-card">
        <div className="ap-due">
          <span className="ap-due-label">Ready by</span>
          <span className="ap-due-value">{formatDue(dueAt(plan))}</span>
        </div>
        <div className="eob-timeline">
          <div className="eob-timeline-step">
            <div className="eob-timeline-connector">
              <div className="eob-timeline-dot dot-done" />
              <div className="eob-timeline-line" />
            </div>
            <div className="eob-timeline-label">
              Case file received
              <div className="eob-timeline-sub">{formatDate(plan.submitted_at ?? plan.created_at)}</div>
            </div>
          </div>
          <div className="eob-timeline-step">
            <div className="eob-timeline-connector">
              <div className="eob-timeline-dot dot-active" />
              <div className="eob-timeline-line" />
            </div>
            <div className="eob-timeline-label active">
              Plan being written
              <div className="eob-timeline-sub">
                {plan.status === 'in_review' ? 'Your bill is being investigated' : 'Next in the queue'}
              </div>
            </div>
          </div>
          <div className="eob-timeline-step">
            <div className="eob-timeline-connector">
              <div className="eob-timeline-dot dot-pending" />
            </div>
            <div className="eob-timeline-label pending">
              Plan delivered
              <div className="eob-timeline-sub">
                {email ? `Emailed to ${email}` : 'Emailed to you'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section label and its one action share a row, so the list is not
          bracketed by two paragraphs of text. File names truncate: the ones
          off a phone camera are long enough to swamp the block otherwise. */}
      <div className="ap-docs">
        <div className="ap-docs-head">
          <span className="ap-docs-label">Your documents</span>
          <button type="button" className="ap-link" onClick={() => inputRef.current?.click()} disabled={adding}>
            {adding ? 'Uploading…' : 'Add a document'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_DOC_EXTENSIONS}
            style={{ display: 'none' }}
            onChange={(e) => { void addDocument(e.target.files); e.target.value = ''; }}
          />
        </div>
        {plan.documents.map((d) => (
          <div key={d.path} className="eob-file-row ap-doc-row">
            <div className="eob-file-icon">
              <FileText size={16} color="#E97A50" />
            </div>
            <div className="ap-doc-text">
              <div className="eob-file-row-name" title={d.name}>{d.name}</div>
              <div className="eob-file-row-meta">
                {d.kind === 'bill' ? 'Bill' : d.kind === 'eob' ? 'Explanation of Benefits' : 'Document'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   3. DELIVERED: the plan
══════════════════════════════════════════════════════════ */
function DeliveredPlan({ plan }: { plan: ActionPlanRow }) {
  const { showToast } = useToast();
  const blocks = useMemo(() => parsePlanMarkdown(plan.plan_body), [plan.plan_body]);
  const viewedRef = useRef<string | null>(null);

  useEffect(() => {
    if (viewedRef.current === plan.id) return;
    viewedRef.current = plan.id;
    phActionPlanViewed({ planId: plan.id });
  }, [plan.id]);

  async function open(path: string) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60);
    if (error || !data?.signedUrl) {
      showToast('Could not open that file. Please try again.', 'error');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener');
  }

  return (
    <>
      <span className="eob-eyebrow">Your plan</span>
      <h1 className="eob-heading">Here is how to <span className="italic">take this bill on.</span></h1>
      {plan.plan_summary && <p className="eob-subheading">{plan.plan_summary}</p>}
      <p className="ap-quiet">
        Delivered {formatDay(plan.delivered_at ?? plan.updated_at)}
        {plan.intake?.provider_name ? ` for your ${plan.intake.provider_name} bill` : ''}.
      </p>

      <PlanBody blocks={blocks} />

      {plan.attachments.length > 0 && (
        <div className="ap-docs">
          <div className="ap-docs-label">Letters and forms, ready to send</div>
          {plan.attachments.map((a) => (
            <button key={a.path} type="button" className="eob-file-row ap-doc-row ap-doc-btn" onClick={() => open(a.path)}>
              <div className="eob-file-icon">
                <FileText size={16} color="#E97A50" />
              </div>
              <div className="ap-doc-text">
                <div className="eob-file-row-name" title={a.name}>{a.name}</div>
                <div className="eob-file-row-meta">Open</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <p className="ap-quiet">
        Questions about a step? Reply to the delivery email or write to{' '}
        <a href="mailto:help@mediloop.ai" className="ap-link">help@mediloop.ai</a>.
      </p>
    </>
  );
}
